import {ValueOf} from "../types/value-of.type";
import {StringHelper} from "./string.helper";

export class ObjectHelper {

    public static isClass(value: any): boolean {
        return typeof value === "function" && !!value.prototype && value.prototype.constructor === value;
    }

    public static base64(integer: number): string {
        const digits = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz+-".split(``);
        let result = ``;

        while (true) {
            result = digits[integer & 0x3f] + result;
            integer >>>= 6;
            if (integer === 0)
                break;
        }

        return result;
    }

    public static deepEqual(objectA: unknown, objectB: unknown): boolean {
        objectA = ObjectHelper.getIdentifyingValue(objectA);
        objectB = ObjectHelper.getIdentifyingValue(objectB);

        if (objectA === objectB) {
            return true;
        }

        if (!(
            (typeof objectA === "object" && objectA !== null)
            &&
            (typeof objectB === "object" && objectB !== null)
        )) {
            return false;
        }

        const objectAKeys = Object.keys(objectA as object);
        const objectBKeys = Object.keys(objectB as object);

        if (objectAKeys.length !== objectBKeys.length) {
            return false;
        }

        return objectAKeys.every(key => {
            const objectAValue = (objectA as any)[key] as unknown;
            const objectBValue = (objectB as any)[key] as unknown;
            return objectBKeys.includes(key) && this.deepEqual(objectAValue, objectBValue)
        });
    }

    public static get<T extends Object>(obj: T, path: keyof T | string): ValueOf<T> {
        return path.toString()
            .split(`.`)
            .reduce((acc: any, part) => acc && ObjectHelper.getPropertyValue(acc, part), obj) as ValueOf<T>;
    };

    public static getAll<T extends Object>(obj: Object, path: string): Array<T> {
        if (!obj) {
            return [obj];
        }

        const parts = path.toString().split(`.`);
        const part = parts.shift()!;

        if (!(part in obj)) {
            throw new Error(`part ${part} of path ${path} not accessible`);
        }

        // @ts-ignore
        let objValue = obj[part] as T;

        path = parts.join(`.`);
        if (Array.isArray(objValue)) {
            if (parts.length < 1) {
                return objValue;
            }

            return objValue.flatMap(objValueItem => ObjectHelper.getAll<T>(objValueItem, path));
        }

        if (parts.length < 1) {
            return [objValue];
        }

        return ObjectHelper.getAll(objValue, path);
    }

    public static getIdentifyingValue(obj: any): unknown | null | undefined {
        if ([null, undefined].includes(obj)) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return `[${obj.map(i => ObjectHelper.getIdentifyingValue(i)).sort().join('|')}]`;
        }

        if (typeof obj.valueOf === "function") {
            const valueOfObject = obj.valueOf();
            const className = Object.getPrototypeOf(valueOfObject).constructor.name;
            if (className !== "Object") {
                return valueOfObject;
            }
        }

        try {
            return JSON.stringify(obj);
        } catch (error) {
            console.error(`JSON.stringify fails for`, obj, {error});
        }

        return obj;
    }

    public static getPropertyValue<T>(obj: T, key: keyof T): T[keyof T] {
        return obj[key];
    }

    public static hasDiffByProperty(objectA: Object, objectB: Object) {

        const recordA = Array.from(Object.entries(objectA));
        const recordB = new Map(Array.from(Object.entries(objectB)));

        for (const [key, value] of recordA) {
            if (!recordB.has(key) || recordB.get(key) !== value) {
                return true;
            }
        }

        return false;
    }

    public static isPromise(value: any): boolean {
        return !!value && typeof value.then === 'function';
    }

    public static isProxy(obj: any): boolean {
        try {
            return obj && typeof obj === 'object' && obj['__isProxy'] === true;
        } catch (e) {
            return false;
        }
    }

    public static hash(obj: any): string | null {

        let hashNumber = 0;
        try {
            const chars = JSON.stringify(obj)

            if (chars.length === 0) {
                return ``;
            }

            for (let i = 0; i < chars.length; i++) {
                const char = chars.charCodeAt(i);
                hashNumber = ((hashNumber << 5) - hashNumber) + char;
                hashNumber = hashNumber & hashNumber;
            }
        } catch (e) {
            console.error(e, {obj});
            return null;
        }

        if (hashNumber === 0) {
            return ``;
        }

        return this.base64(hashNumber);
    }

    public static isEqual(objA: any, objB: any): boolean {
        const idA = "" + ObjectHelper.getIdentifyingValue(objA);
        const idB = "" + ObjectHelper.getIdentifyingValue(objB);

        return idA.localeCompare(idB) === 0;
    }

    public static set<TValue, TObject extends object = object>(target: TObject, path: string, value: TValue): void {

        const posOfDivider = path.indexOf(`.`);
        const partialPath = (posOfDivider < 0) ? path : path.substring(0, posOfDivider);
        if (posOfDivider < 0) {
            try {
                ObjectHelper.setPropertyValue(target, partialPath as keyof object, value as ValueOf<TObject>);
            } catch (err) {
                console.error(`Konnte Pfad «${partialPath}» nicht in ${StringHelper.toKebabCase(target.constructor.name)} finden.`);
            }
        } else {
            const subObject = ObjectHelper.get(target, partialPath);
            if (subObject) {
                ObjectHelper.set(subObject, path.substring(posOfDivider + 1), value);
            }
        }
    }

    public static setPropertyValue<T>(obj: T, key: keyof T, value: T[keyof T]): void {
        const oldValue = obj[key];
        // @ts-ignore
        obj[key] = Array.isArray(oldValue) ? [...oldValue, value] : value;
    }

    static flatten(errors: { [p: string]: { [p: string]: string[] } }): { [p: string]: string[] } {

        const result: { [p: string]: string[] } = {};

        for (const outerKey in errors) {
            if (errors.hasOwnProperty(outerKey)) {
                const innerValue = errors[outerKey];
                if (Array.isArray(innerValue)) {
                    result[outerKey] = innerValue;
                } else {
                    for (const innerKey in innerValue) {
                        if (innerValue.hasOwnProperty(innerKey)) {
                            const combinedKey = `${outerKey}.${innerKey}`;
                            result[combinedKey] = innerValue[innerKey];
                        }
                    }
                }
            }
        }

        return result;
    }
}